import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { 
    APP_INITIALIZER, 
    ApplicationConfig, 
    inject, 
    provideZonelessChangeDetection,
    CSP_NONCE
} from '@angular/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    BaseRouteReuseStrategy,
    provideRouter,
    RouteReuseStrategy,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
} from '@angular/router';
import { CustomPreloadingStrategy } from 'app/core/preloading/custom-preloading-strategy';
import { mockApiServices, provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { catchError, defaultIfEmpty, firstValueFrom, of } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { authInterceptor } from 'app/core/auth/auth.interceptor';
import { fuseLoadingInterceptor } from '@fuse/services/loading';

export const appConfig: ApplicationConfig = {
    providers: [
        // Zoneless Change Detection for optimal performance
        // Angular 21 supports zoneless change detection for better performance
        // This eliminates the overhead of Zone.js and relies on signals and explicit updates
        provideZonelessChangeDetection(),
        provideAnimations(),
        
        // Single HTTP client configuration with all interceptors
        // Interceptors run in order: auth first, then loading
        provideHttpClient(
            withInterceptors([authInterceptor, fuseLoadingInterceptor])
        ),
       provideRouter(
            appRoutes,
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            withPreloading(CustomPreloadingStrategy), // Smart preloading based on route data
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        {
            provide: RouteReuseStrategy,
            useClass: class extends BaseRouteReuseStrategy {
                override shouldReuseRoute(): boolean {
                    return false;
                }
            }
        },
        {
            provide: CSP_NONCE,
            useFactory: () => {
                const metaNonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') || '';
                if (metaNonce && metaNonce !== '__CSP_NONCE__') {
                    return metaNonce;
                }

                return document.cookie
                    .split('; ')
                    .find((row) => row.startsWith('csp-nonce='))
                    ?.slice('csp-nonce='.length) || '';
            }
        },
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    // {
                    //     id: 'tr',
                    //     label: 'Turkish',
                    // },
                    {
                        id: 'ph',
                        label: 'Tagalog',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () =>
                    firstValueFrom(
                        translocoService.load(defaultLang).pipe(
                            defaultIfEmpty({}),
                            catchError((error) => {
                                console.error(
                                    'Failed to load default translations',
                                    error
                                );
                                return of({});
                            })
                        )
                    );
            },
            multi: true,
        },

        // Fuse (without HTTP client - already provided above)
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
